import {
  type CoachingScope,
  getCoachees,
  getCoachingActions,
  getCoachingNotes,
  getCoachPreRead,
  getCoachRoster,
  getCoachSessions,
  getCoachSettings,
  queryKeys,
} from "@healthos/client-data";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { client } from "../../api/healthos";

/** Every note on every session in the relationship, read once per page. A coach passes the coachee's id. */
export function useCoachingNotes(scope?: CoachingScope) {
  return useQuery({
    queryKey: queryKeys.coachingNotes(scope),
    queryFn: () => getCoachingNotes(client(), scope),
  });
}

export function useCoachingActions(scope?: CoachingScope) {
  return useQuery({
    queryKey: queryKeys.coachingActions(scope),
    queryFn: () => getCoachingActions(client(), scope),
  });
}

export function useCoachSettings(enabled = true) {
  return useQuery({
    queryKey: queryKeys.coachSettings,
    queryFn: () => getCoachSettings(client()),
    enabled,
  });
}

/** The coach's booked sessions between two instants. */
export function useCoachSessions(
  from: string,
  to: string,
  options: { enabled?: boolean; keepPrevious?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.coachSessions(from, to),
    queryFn: () => getCoachSessions(client(), from, to),
    enabled: options.enabled,
    placeholderData: options.keepPrevious ? keepPreviousData : undefined,
  });
}

export function useCoachPreRead(coacheeId: string) {
  return useQuery({
    queryKey: queryKeys.coachPreRead(coacheeId),
    queryFn: () => getCoachPreRead(client(), coacheeId),
  });
}

export function useCoachCoachees() {
  return useQuery({ queryKey: queryKeys.coachCoachees, queryFn: () => getCoachees(client()) });
}

/** Every coach with their caseload, for Coaching Ops. */
export function useCoachRoster() {
  return useQuery({
    queryKey: queryKeys.coachingOpsCoaches,
    queryFn: () => getCoachRoster(client()),
  });
}
